<template>
  <section class="max-w-2xl space-y-4">
    <header>
      <h1 class="text-2xl font-semibold text-gray-900">Account</h1>
      <p class="mt-1 text-sm text-gray-500">Your sign-in details and personal display preferences.</p>
    </header>

    <div class="card space-y-4">
      <div class="flex items-center gap-3">
        <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">
          {{ userInitial }}
        </span>
        <div class="min-w-0">
          <p class="m-0 truncate text-sm font-semibold text-gray-900">{{ user?.name || user?.email || 'Account' }}</p>
          <span class="badge bg-blue-50 text-blue-700">{{ roleLabel }}</span>
          <ul v-if="permissions.length" class="m-0 mt-2 flex list-none flex-wrap gap-1 p-0" aria-label="Your permissions">
            <li v-for="permission in permissions" :key="permission" class="badge bg-gray-100 font-mono text-gray-700">{{ permission }}</li>
          </ul>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <span class="label">Name</span>
          <p class="m-0 text-sm text-gray-900">{{ user?.name || '—' }}</p>
        </div>
        <div>
          <span class="label">Email</span>
          <p class="m-0 text-sm text-gray-900">{{ user?.email || '—' }}</p>
        </div>
        <div>
          <span class="label">Nickname</span>
          <p class="m-0 text-sm text-gray-900">{{ user?.nickname || '—' }}</p>
        </div>
      </div>

      <div class="flex justify-end pt-2">
        <router-link :to="{ name: 'Logout' }" class="btn-secondary">Sign out</router-link>
      </div>
    </div>

    <div v-if="canSeeCustomerMappings" class="card space-y-3">
      <div>
        <h2 class="m-0 text-sm font-semibold text-gray-900">Calendar settings</h2>
        <p class="mt-1 mb-0 text-xs text-gray-500">Applies to this browser only — other people's calendars are unaffected.</p>
      </div>

      <label class="flex items-start gap-2 text-sm text-gray-900">
        <input v-model="hideCustomerMappings" class="mt-0.5" type="checkbox" />
        <span>
          Hide customer names and colors on the capacity calendar
          <span class="mt-0.5 block text-xs text-gray-500">Hides the customer legend, mapping colors, and the customer line in PO tooltips, so you can share your screen.</span>
        </span>
      </label>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { useAuth0 } from '@auth0/auth0-vue';
  import { computed } from 'vue';

  import { useAccess } from '@/composables/useAccess';
  import { useAccessStore } from '@/store/accessStore';
  import { useCalendarPrivacyStore } from '@/store/calendarPrivacyStore';
  import { PARTNER_ROLE, SUPERADMIN_ROLE } from '@/types/access';

  const ROLE_LABELS: Record<string, string> = {
    [SUPERADMIN_ROLE]: 'Superadmin',
    [PARTNER_ROLE]: 'Partner',
  };

  const { user } = useAuth0();
  const { canSeeCustomerMappings } = useAccess();
  const accessStore = useAccessStore();
  const privacyStore = useCalendarPrivacyStore();

  const userInitial = computed(() => (user.value?.name ?? user.value?.email ?? 'A').charAt(0).toUpperCase());
  const roleLabel = computed(() => {
    const role = accessStore.me?.role;
    return (role && ROLE_LABELS[role]) || 'Custom role';
  });
  const permissions = computed(() => accessStore.me?.permissions ?? []);
  const hideCustomerMappings = computed({
    get: () => privacyStore.hideCustomerMappings,
    set: (value: boolean) => privacyStore.setHideCustomerMappings(value),
  });
</script>
